import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('rw_token'));
  const [loading, setLoading] = useState(true);

  const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Add token to requests
  api.interceptors.request.use((config) => {
    const storedToken = localStorage.getItem('rw_token');
    if (storedToken) {
      config.headers.Authorization = `Bearer ${storedToken}`;
    }
    return config;
  });

  // Handle 401 errors
  api.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        logout();
      }
      return Promise.reject(error);
    }
  );

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('rw_token');
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('rw_token', access_token);
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('rw_token');
    setToken(null);
    setUser(null);
  };

  const isSuperAdmin = () => user?.role === 'super_admin';
  const isAdminFranchise = () => user?.role === 'admin_franchise';
  const isCommercial = () => user?.role === 'commercial';
  const isTechnician = () => user?.role === 'technician';
  const isReadOnly = () => user?.role === 'read_only';
  const canManageUsers = () => isSuperAdmin() || isAdminFranchise();
  const canSeePrices = () => isSuperAdmin();
  const canEdit = () => user?.role !== 'read_only';

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        logout,
        api,
        isSuperAdmin,
        isAdminFranchise,
        isCommercial,
        isTechnician,
        isReadOnly,
        canManageUsers,
        canSeePrices,
        canEdit,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
